import { router, usePage } from "@inertiajs/react";
import {
	Activity,
	Ban,
	Cctv,
	Clock3,
	LoaderIcon,
	MapPinned,
	NotebookPen,
	RefreshCw,
	Stethoscope,
} from "lucide-react";
import { type ComponentProps, memo, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, populateQueryParams } from "@/lib/utils";
import type { AppointmentIndexGlobalPageProps } from "@/pages/AdminPortal/Appointment/Index";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { GlobalPageProps } from "@/types/globals";
import { useReconcileBonus } from "./reconcile-bonus";

// A completed visit that already holds both its SOAP and its evidence is finished
// paperwork: nothing that touches it — its status, its schedule, its address, its medical
// record, or cancelling it — stays open, because any of those would strand the documents
// against a visit they no longer describe. Flags come from the list payloads
// (has_soap/has_evidence); the detail payload carries the documents themselves instead, so
// fall back to those when the flags are absent.
//
// Compared case-insensitively on purpose. Every payload today sends the Rails enum KEY
// ("completed"), but the same screens also carry Order#status as its raw database value
// ("COMPLETED"), and the per-visit hashes on the Orders page are hand-built — one copied
// line spelling this the other way would silently unlock every locked visit, with nothing
// failing loudly to say so.
export const isDocumentLocked = (appt: Appointment) =>
	appt.status?.toLowerCase() === "completed" &&
	(appt.hasSoap ?? !!appt.soap) &&
	(appt.hasEvidence ?? !!appt.evidence);

// A gate answers with WHY an action is closed — an i18n key under the appointments
// namespace, rendered by DisabledReasonTooltip — rather than a boolean, so the greyed-out
// state and the tooltip explaining it cannot disagree. null means the action is open.
export type BlockReason = string | null;

export const REASON = {
	soapNotAllowed: "button.edit_soap_not_allowed",
	deleteNotAllowed: "button.delete_document_not_allowed",
	visitLocked: "button.disabled_reason.visit_locked",
	noSoap: "button.disabled_reason.no_soap",
	noEvidence: "button.disabled_reason.no_evidence",
	notLastVisit: "button.disabled_reason.not_last_visit",
	noInvoice: "button.disabled_reason.no_invoice",
	notBonusRo: "button.disabled_reason.not_bonus_ro",
	packageStopped: "button.disabled_reason.order_partially_completed",
} as const;

// Hentikan Paket already refunded the visits it cancelled, so no visit of a stopped
// order may change status. Takes the order's status, uppercase as it arrives.
export const packageStoppedReason = (orderStatus?: string): BlockReason =>
	orderStatus === "PARTIALLY_COMPLETED" ? REASON.packageStopped : null;

// When several causes apply, the first one listed wins: callers pass them whitelist →
// order status → visit lock → missing document → anything else.
export const firstReason = (...reasons: BlockReason[]): BlockReason =>
	reasons.find(Boolean) ?? null;

const lockReason = (appt: Appointment): BlockReason =>
	isDocumentLocked(appt) ? REASON.visitLocked : null;

// Deleting needs the whitelist and then the document itself, since upstream answers 404
// for a visit without it.
export const deleteDocumentReason = (
	kind: "soap" | "evidence",
	has: boolean,
	canUpdateSoap: boolean,
): BlockReason =>
	firstReason(
		canUpdateSoap ? null : REASON.deleteNotAllowed,
		has ? null : kind === "soap" ? REASON.noSoap : REASON.noEvidence,
	);

// * helper function
// * bypass this because it's for our admin internal uses
export const blockReason = {
	// updateStatus: (appt: Appointment) =>
	// 	appt.status !== "completed" &&
	// 	appt.status !== "unscheduled" &&
	// 	appt.status !== "on_hold",
	// cancel: (appt: Appointment) => appt.status !== "completed",
	// createSeries: (appt: Appointment) =>
	// 	appt.totalPackageVisits > 1 &&
	// 	appt.visitNumber !== appt.totalPackageVisits &&
	// 	appt.nextVisitProgress,
	// reschedule: (appt: Appointment) => appt.status !== "completed",
	// Every other status stays open, cancelled included.
	updateStatus: (appt: Appointment): BlockReason =>
		firstReason(packageStoppedReason(appt.order?.status), lockReason(appt)),
	cancel: lockReason,
	createSeries: (_appt: Appointment): BlockReason => null,
	reschedule: lockReason,
	updateAddress: lockReason,
	editMedicalRecord: lockReason,
	// The one write the lock deliberately does not cover. Everything above moves the
	// visit, which would leave its documents describing something else; correcting the
	// notes themselves moves nothing. Gated on having a SOAP at all instead, because
	// upstream answers 404 for a visit with none.
	editSoap: (appt: Appointment): BlockReason =>
		(appt.hasSoap ?? !!appt.soap) ? null : REASON.noSoap,
};

// The whitelist goes first: an admin outside it cannot open the action on any visit.
export const editSoapReason = (appt: Appointment, canUpdateSoap: boolean) =>
	firstReason(
		canUpdateSoap ? null : REASON.soapNotAllowed,
		blockReason.editSoap(appt),
	);

// Update SOAP is limited to super admins and the emails on config / UPDATE_SOAP_WHITELIST;
// the API answers 403 to everyone else, so every entry point greys out to match.
export const useCanUpdateSoap = () =>
	usePage<GlobalPageProps>().props.adminPortal?.featureFlags?.canUpdateSoap ??
	false;

export const useCanReconcileBonus = () =>
	usePage<GlobalPageProps>().props.adminPortal?.featureFlags
		?.canReconcileBonus ?? false;

// ACTION_MENU_REGROUP: the row menus' regrouped layout (Download submenu, bulk actions
// inside their per-visit submenus). Off, the menus keep the layout admins already know.
export const useActionMenuRegroup = () =>
	usePage<GlobalPageProps>().props.adminPortal?.featureFlags
		?.actionMenuRegroupEnabled ?? false;

// Says why an action is greyed out. A disabled Radix item or button takes no pointer
// events, so the tooltip hangs off a wrapper instead — and only when there is a reason, so
// an open action renders untouched. `className` lands on that wrapper, which takes the
// child's place in the layout while it is there.
export function DisabledReasonTooltip({
	reason,
	side,
	className,
	children,
}: {
	reason?: BlockReason;
	side?: "top" | "right" | "bottom" | "left";
	className?: string;
	children: ReactNode;
}) {
	const { t } = useTranslation("appointments");
	if (!reason) return children;

	return (
		<TooltipProvider delayDuration={0}>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className={cn("block", className)}>{children}</span>
				</TooltipTrigger>
				<TooltipContent side={side} className="max-w-64">
					{t(reason)}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

function ReconcileBonusButton({
	orderId,
	className,
	size,
}: {
	orderId?: string | null;
	className: string;
	size: AppointmentActionButtonsProps["buttonSize"];
}) {
	const { pending, disabled, reconcile, label } = useReconcileBonus(orderId);

	return (
		<Button
			variant="primary-outline"
			className={className}
			size={size}
			disabled={disabled}
			onClick={(event) => {
				event.preventDefault();
				event.stopPropagation();

				reconcile();
			}}
		>
			{pending ? <LoaderIcon className="animate-spin" /> : <RefreshCw />}
			{label}
		</Button>
	);
}

export function ReconcileBonusMenuItem({
	orderId,
}: {
	orderId?: string | null;
}) {
	const { disabled, reconcile, label } = useReconcileBonus(orderId);

	return (
		<DisabledReasonTooltip
			reason={orderId ? null : REASON.notBonusRo}
			side="left"
		>
			<DropdownMenuItem onSelect={reconcile} disabled={disabled}>
				<RefreshCw className="opacity-60" aria-hidden="true" />
				{label}
			</DropdownMenuItem>
		</DisabledReasonTooltip>
	);
}

// * core component
interface AppointmentActionButtonsProps extends ComponentProps<"div"> {
	schedule: Appointment;
	isExpanded: boolean;
	buttonSize?: "xs" | "sm" | "default" | "lg" | "xl";
}

const AppointmentActionButtons = memo(function Component({
	className,
	schedule,
	isExpanded: _isExpanded,
	buttonSize = "default",
}: AppointmentActionButtonsProps) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const canUpdateSoap = useCanUpdateSoap();
	const canReconcileBonus = useCanReconcileBonus();

	const blocked = useMemo(
		() => ({
			updateStatus: blockReason.updateStatus(schedule),
			cancel: blockReason.cancel(schedule),
			reschedule: blockReason.reschedule(schedule),
			updateAddress: blockReason.updateAddress(schedule),
			editMedicalRecord: blockReason.editMedicalRecord(schedule),
			editSoap: editSoapReason(schedule, canUpdateSoap),
		}),
		[schedule, canUpdateSoap],
	);
	// These buttons live inside the ?view_appointment drawer, so every target URL has to
	// drop that param — router.get merges data into the existing query string, which
	// would otherwise leave both the detail panel and the form rendered at once.
	const openForm = (params: Record<string, string | null>, only: string[]) => {
		const { fullUrl } = populateQueryParams(pageURL, {
			view_appointment: null,
			...params,
		});

		router.get(
			fullUrl,
			{},
			{
				only: ["adminPortal", "flash", "errors", ...only],
				preserveScroll: true,
				preserveState: true,
				replace: false,
			},
		);
	};
	const routeTo = {
		cancel: (id: string) => openForm({ cancel: id }, ["selectedAppointment"]),
		updatePic: (id: string) =>
			openForm({ update_pic: id }, ["selectedAppointment", "optionsData"]),
		updateAddress: (id: string) =>
			openForm({ update_address: id }, [
				"selectedAppointment",
				"filterOptionsData",
			]),
		editMedicalRecord: (id: string) =>
			openForm({ edit_medical_record: id }, ["selectedAppointment"]),
		editSoap: (id: string) =>
			openForm({ edit_soap: id }, ["selectedAppointment"]),
		updateStatus: (id: string) =>
			openForm({ update_status: id }, ["selectedAppointment", "optionsData"]),
		reschedule: (id: string) => {
			const urlSource =
				globalProps.adminPortal.router.adminPortal.appointment.index;
			const url = `${urlSource}/${id}/reschedule`;

			router.visit(url);
		},
		createSeries: (apptSource: Appointment) => {
			const urlSource =
				globalProps.adminPortal.router.adminPortal.appointment.new;
			const idSource = apptSource?.appointmentReferenceId || apptSource.id;
			const { fullUrl } = populateQueryParams(urlSource, {
				reference: idSource,
			});

			router.visit(fullUrl);
		},
	};

	// isActive alone decides whether Cancel is rendered — an already-cancelled visit has
	// nothing left to cancel. The document lock only greys it out, so a locked visit still
	// shows the action it no longer allows rather than quietly dropping it.
	const isActive = schedule.status !== "cancelled";
	// Every action used to carry the same weight. Three tiers now: record edits are quiet,
	// scheduling actions carry a border, and the destructive one is held at the opposite
	// end of the row — behind a rule, at the bottom, on touch widths.
	// Below lg the actions sit at the top of the panel and are thumb-targets: 44px tall,
	// two per row so six of them don't eat a third of the screen. From lg they collapse
	// back into a compact right-aligned toolbar.
	const stack = "grid grid-cols-2 gap-2 lg:flex lg:gap-2";
	// w-full keeps a button filling its cell while a DisabledReasonTooltip wrapper holds it.
	const action =
		"h-11 w-full justify-start px-3 text-sm lg:h-8 lg:w-auto lg:justify-center lg:px-3 lg:text-xs";
	// A borderless button reads as a label when it is the first thing in the panel, so the
	// quiet tier borrows a surface on touch widths and drops it again in the lg toolbar.
	const quiet = "border border-border bg-card lg:border-0 lg:bg-transparent";

	return (
		<div className={cn("flex flex-col w-full gap-2", className)}>
			<div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end lg:gap-3">
				{/* destructive — first in the DOM so mr-auto can hold it at the far left of
				    the lg row, order-last so it drops below everything on touch widths */}
				{isActive && (
					<>
						<Separator className="order-last mt-1 lg:hidden" />

						<DisabledReasonTooltip
							reason={blocked.cancel}
							className="order-last lg:order-none lg:mr-auto"
						>
							<Button
								variant="ghost-destructive"
								className={cn(action, "order-last lg:order-none lg:mr-auto")}
								size={buttonSize}
								disabled={!!blocked.cancel}
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();

									routeTo.cancel(String(schedule.id));
								}}
							>
								<Ban />
								{t("button.cancel_booking")}
							</Button>
						</DisabledReasonTooltip>
					</>
				)}

				{/* record edits — lowest weight, most frequent */}
				<div className={stack}>
					<Button
						variant="ghost"
						className={cn(action, quiet)}
						size={buttonSize}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();

							routeTo.updatePic(String(schedule.id));
						}}
					>
						<Cctv />
						{t("button.update_pic")}
					</Button>

					<DisabledReasonTooltip reason={blocked.updateAddress}>
						<Button
							variant="ghost"
							className={cn(action, quiet)}
							size={buttonSize}
							disabled={!!blocked.updateAddress}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();

								routeTo.updateAddress(String(schedule.id));
							}}
						>
							<MapPinned />
							{t("button.update_address")}
						</Button>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip
						reason={blocked.editMedicalRecord}
						className="col-span-2 lg:col-auto"
					>
						<Button
							variant="ghost"
							className={cn(action, quiet, "col-span-2 lg:col-auto")}
							size={buttonSize}
							disabled={!!blocked.editMedicalRecord}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();

								routeTo.editMedicalRecord(String(schedule.id));
							}}
						>
							<Stethoscope />
							{t("button.edit_medical_record")}
						</Button>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip
						reason={blocked.editSoap}
						className="col-span-2 lg:col-auto"
					>
						<Button
							variant="ghost"
							className={cn(action, quiet, "col-span-2 lg:col-auto")}
							size={buttonSize}
							disabled={!!blocked.editSoap}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();

								routeTo.editSoap(String(schedule.id));
							}}
						>
							<NotebookPen />
							{t("button.edit_soap")}
						</Button>
					</DisabledReasonTooltip>
				</div>

				{/* scheduling — the actions that move the visit. Always rendered: Update
				    Status is open on every status (a locked one reads better disabled than
				    missing), only Reschedule stays behind isActive. */}

				<Separator orientation="vertical" className="hidden h-5 lg:block" />

				<div className={stack}>
					{isActive && (
						<DisabledReasonTooltip reason={blocked.reschedule}>
							<Button
								variant="primary-outline"
								className={action}
								size={buttonSize}
								disabled={!!blocked.reschedule}
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();

									routeTo.reschedule(String(schedule.id));
								}}
							>
								<Clock3 />
								{t("button.reschedule")}
							</Button>
						</DisabledReasonTooltip>
					)}

					<DisabledReasonTooltip reason={blocked.updateStatus}>
						<Button
							variant="primary-outline"
							className={action}
							size={buttonSize}
							disabled={!!blocked.updateStatus}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();

								routeTo.updateStatus(String(schedule.id));
							}}
						>
							<Activity />
							{t("button.update_status")}
						</Button>
					</DisabledReasonTooltip>

					{canReconcileBonus && (
						<DisabledReasonTooltip
							reason={schedule.reconcilableOrderId ? null : REASON.notBonusRo}
							className="col-span-2 lg:col-auto"
						>
							<ReconcileBonusButton
								orderId={schedule.reconcilableOrderId}
								className={cn(action, "col-span-2 lg:col-auto")}
								size={buttonSize}
							/>
						</DisabledReasonTooltip>
					)}
				</div>
			</div>
		</div>
	);
});

export default AppointmentActionButtons;
